import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  useGetProjectAccessQuery,
  usePutAssetMutation,
  useGetAssetPhotoUploadUrlMutation,
  useDeleteAssetPhotoMutation,
} from '../../features/api/apiSlice';
import {
  Modal,
  Input,
  DatePicker,
  Select,
  Tooltip,
  Divider,
  Button,
  Upload,
  message,
  Tag,
} from 'antd';
import dayjs from 'dayjs';
import { CalendarIcon, User, Notebook } from 'lucide-react';
import { InfoCircleOutlined, InboxOutlined, DeleteOutlined } from '@ant-design/icons';
import './EditGeneral.css';
import { v4 as uuidv4 } from 'uuid';

import { getPhotoDisplayUrl, createPhotoObject } from '../../utils/photoUtilities';
import { uploadPhotoViaPresignedUrl, resolveUploadBody } from '../../services/s3PhotoUpload';

const PHOTO_STATUS = {
  UPLOADING: 'uploading',
  UPLOADED: 'uploaded',
  ERROR: 'error',
  DELETING: 'deleting',
};

const STATUS_STYLES = {
  [PHOTO_STATUS.UPLOADING]: { color: 'gold', label: 'Uploading' },
  [PHOTO_STATUS.UPLOADED]: { color: 'green', label: 'Uploaded' },
  [PHOTO_STATUS.ERROR]: { color: 'red', label: 'Error' },
  [PHOTO_STATUS.DELETING]: { color: 'orange', label: 'Removing' },
};

const { TextArea } = Input;

const EditGeneral = ({ isModalOpen, setModalOpen, selectedAsset, refetch }) => {
  const [photosMeta, setPhotosMeta] = useState([]);
  const [originalExistingPhotos, setOriginalExistingPhotos] = useState([]);
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const photosMetaRef = useRef([]);
  const hasSubmittedRef = useRef(false);
  const cleanupDoneRef = useRef(false);
  const assetInfoRef = useRef({ id: null, project_id: null, fullAsset: null });

  const [formState, setFormState] = useState({
    name: '',
    note: '',
    next_maintenance: null,
    installed_at: null,
    created_by: null,
  });
  const [updateAsset, { isLoading }] = usePutAssetMutation();
  const [getAssetPhotoUploadUrl] = useGetAssetPhotoUploadUrlMutation();
  const [deleteAssetPhoto] = useDeleteAssetPhotoMutation();
  const [draftKey, setDraftKey] = useState(() => uuidv4());
  const { data: accessData } = useGetProjectAccessQuery(selectedAsset?.project_id, {
    skip: !selectedAsset,
  });

  const hasUploadingPhotos = photosMeta.some((photo) => photo.status === PHOTO_STATUS.UPLOADING);

  useEffect(() => {
    photosMetaRef.current = photosMeta;
  }, [photosMeta]);

  const userOptions = accessData?.access?.map((user) => {
    if (user?.first_name && user?.last_name && user.user_id)
      return {
        label: `${user.first_name} ${user.last_name}`,
        value: user.user_id,
      };
    else if (user.email && user.user_id)
      return {
        label: user.email,
        value: user.user_id,
      };
    else return {};
  });

  const cleanupDraftUploads = useCallback(
    async (photoList) => {
      const deletable = (photoList || [])
        .filter(
          (photo) =>
            photo.id &&
            !photo.isExisting &&
            (photo.status === PHOTO_STATUS.UPLOADED || photo.status === PHOTO_STATUS.DELETING)
        )
        .map((photo) => photo.id);

      if (!deletable.length) return;

      const projectId = selectedAsset?.project_id || assetInfoRef.current.project_id;
      if (!projectId) {
        console.error('Cannot cleanup photos: project_id is missing');
        message.error('There was an error while cleaning up photos, please try again.');
        return;
      }

      await Promise.all(
        deletable.map((photoId) =>
          deleteAssetPhoto({ projectId, photoId })
            .unwrap()
            .catch((error) => {
              console.error('Failed to delete staged photo', photoId, error);
            })
        )
      );
    },
    [deleteAssetPhoto]
  );

  useEffect(() => {
    if (!isModalOpen) return;
    if (!selectedAsset) return;

    hasSubmittedRef.current = false;
    cleanupDoneRef.current = false;

    assetInfoRef.current = {
      id: selectedAsset.id,
      project_id: selectedAsset.project_id,
      fullAsset: { ...selectedAsset },
    };

    setPhotosToDelete([]);

    const existingPhotos =
      selectedAsset.photos
        ?.map((photo, idx) => {
          const standardPhoto = createPhotoObject({
            ...(typeof photo === 'string' ? { url: photo } : photo),
            fileName: `Photo-${idx + 1}`,
            isExisting: true,
          });

          standardPhoto.tempId = `existing-${photo.id || idx}`;
          standardPhoto.status = PHOTO_STATUS.UPLOADED;

          return standardPhoto;
        })
        .filter(Boolean) || [];

    setDraftKey(uuidv4());
    setPhotosMeta(existingPhotos);
    setOriginalExistingPhotos([...existingPhotos]);

    setFormState({
      name: selectedAsset.name || '',
      note: selectedAsset.note || '',
      next_maintenance: selectedAsset.next_maintenance
        ? new Date(selectedAsset.next_maintenance)
        : null,
      installed_at: selectedAsset.installed_at ? new Date(selectedAsset.installed_at) : null,
      created_by: selectedAsset.created_by || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, selectedAsset?.id]);

  useEffect(() => {
    if (isModalOpen) return;

    const photosToCleanup = [...photosMetaRef.current];

    setPhotosMeta([]);
    setOriginalExistingPhotos([]);
    setPhotosToDelete([]);
    setFormState({
      name: '',
      note: '',
      next_maintenance: null,
      installed_at: null,
      created_by: null,
    });
    hasSubmittedRef.current = false;

    if (!cleanupDoneRef.current && photosToCleanup.length > 0) {
      cleanupDraftUploads(photosToCleanup);
    }
    cleanupDoneRef.current = false;
    assetInfoRef.current = { id: null, project_id: null, fullAsset: null };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const onChangeField = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const performUpload = useCallback(
    async (tempId, photoData) => {
      const projectId = selectedAsset?.project_id || assetInfoRef.current.project_id;

      if (!projectId || !draftKey) {
        console.error('Cannot upload photo: missing project_id or draftKey', {
          projectId,
          draftKey,
        });
        message.error('There was an error while uploading the photo, please try again.');
        return;
      }

      try {
        const { body, contentType } = resolveUploadBody(photoData);
        const { id } = await uploadPhotoViaPresignedUrl({
          requestUploadUrl: (payload) => getAssetPhotoUploadUrl({ projectId, payload }).unwrap(),
          body,
          contentType,
          uploadPayload: {
            draft_key: draftKey,
            mime_type: contentType,
            file_name: photoData.fileName || photoData.name || 'Photo',
            file_size: photoData.fileSize || (body.size ?? null),
          },
        });

        setPhotosMeta((prev) =>
          prev.map((photo) =>
            photo.tempId === tempId
              ? {
                  ...photo,
                  id,
                  status: PHOTO_STATUS.UPLOADED,
                  uploadStatus: PHOTO_STATUS.UPLOADED,
                }
              : photo
          )
        );
      } catch (error) {
        console.error('Failed to upload photo:', error);
        message.error(
          error?.data?.error || error?.message || 'There was an error: Failed to upload photo.'
        );
        setPhotosMeta((prev) =>
          prev.map((photo) =>
            photo.tempId === tempId
              ? {
                  ...photo,
                  status: PHOTO_STATUS.ERROR,
                  errorMessage: error?.data?.error || error?.message || 'Failed to upload photo',
                }
              : photo
          )
        );
      }
    },
    [selectedAsset?.project_id, draftKey, getAssetPhotoUploadUrl]
  );

  const retryPhotoUpload = useCallback(
    (tempId) => {
      const target = photosMetaRef.current.find((photo) => photo.tempId === tempId);
      if (!target) return;

      setPhotosMeta((prev) =>
        prev.map((photo) =>
          photo.tempId === tempId
            ? { ...photo, status: PHOTO_STATUS.UPLOADING, errorMessage: null }
            : photo
        )
      );

      void performUpload(tempId, target);
    },
    [performUpload]
  );

  const handleSubmit = async () => {
    const currentAsset = selectedAsset || assetInfoRef.current.fullAsset;
    if (!currentAsset || !currentAsset.id || !currentAsset.project_id) {
      message.error('There was an error submitting the asset, please try again.');
      return;
    }

    const payload = Object.entries(formState).reduce((acc, [k, v]) => {
      let val = v;
      if (v instanceof Date) {
        val = dayjs(v).toISOString();
      }
      if (val !== currentAsset[k]) acc[k] = val;
      return acc;
    }, {});

    try {
      if (photosToDelete.length > 0) {
        await Promise.all(
          photosToDelete
            .filter((photo) => photo.id)
            .map((photo) =>
              deleteAssetPhoto({
                projectId: currentAsset.project_id,
                photoId: photo.id,
              })
                .unwrap()
                .catch((error) => {
                  console.error('Failed to delete photo', photo.id, error);
                })
            )
        );
      }

      payload.photo_ids = photosMeta
        .filter((photo) => photo.id && photo.status === PHOTO_STATUS.UPLOADED)
        .map((photo) => photo.id);

      hasSubmittedRef.current = true;
      await updateAsset({
        projectId: currentAsset.project_id,
        assetId: currentAsset.id,
        ...payload,
      }).unwrap();

      setPhotosToDelete([]);
      cleanupDoneRef.current = true; // Prevent cleanup from deleting photos that were just linked

      if (typeof refetch === 'function') {
        try {
          refetch();
        } catch (refetchError) {
          console.error('Error refetching assets:', refetchError);
        }
      }

      message.success('Asset updated successfully');
      setModalOpen(false);
    } catch (err) {
      hasSubmittedRef.current = false;
      console.error('Error updating asset:', err);
    }
  };

  const handleDraggerUpload = (file) => {
    try {
      const tempId = `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
      const newPhoto = {
        tempId,
        id: null,
        file,
        previewUrl: URL.createObjectURL(file),
        fileName: file.name,
        fileType: file.type || 'image/jpeg',
        fileSize: file.size,
        isExisting: false,
        status: PHOTO_STATUS.UPLOADING,
        errorMessage: null,
      };

      setPhotosMeta((prev) => [...prev, newPhoto]);

      const projectId = selectedAsset?.project_id || assetInfoRef.current.project_id;

      if (projectId) {
        void performUpload(tempId, newPhoto);
        message.success('Photo upload started');
      } else {
        console.error('Cannot upload photo: missing project data');
        setPhotosMeta((prev) => prev.filter((photo) => photo.tempId !== tempId));
      }
    } catch (error) {
      console.error('Failed to process photo:', error);
    }

    return false;
  };

  const handleDeletePhoto = async (idx) => {
    const photo = photosMeta[idx];
    if (!photo) return;

    if (photo.status === PHOTO_STATUS.UPLOADING) {
      message.warning('Please wait for the upload to finish before deleting this photo.');
      return;
    }

    if (photo.status === PHOTO_STATUS.DELETING) {
      return;
    }

    if (photo.isExisting) {
      setPhotosToDelete((prev) => [...prev, photo]);
      const updated = photosMeta.filter((_, i) => i !== idx);
      setPhotosMeta(updated);
      message.warning(`Photo will be permanently deleted when you save`);
      return;
    }

    if (photo.id && !photo.isExisting) {
      setPhotosMeta((prev) =>
        prev.map((p, i) => (i === idx ? { ...p, status: PHOTO_STATUS.DELETING } : p))
      );

      try {
        const projectId = selectedAsset?.project_id || assetInfoRef.current.project_id;
        if (!projectId) {
          return;
        }
        await deleteAssetPhoto({
          projectId,
          photoId: photo.id,
        }).unwrap();
        setPhotosMeta((prev) => prev.filter((_, i) => i !== idx));
        message.success('Photo deleted successfully');
      } catch (error) {
        console.error('Failed to delete photo', error);
        setPhotosMeta((prev) =>
          prev.map((p, i) =>
            i === idx ? { ...p, status: PHOTO_STATUS.UPLOADED, errorMessage: null } : p
          )
        );
      }
    } else {
      setPhotosMeta((prev) => prev.filter((_, i) => i !== idx));
      message.success('Photo removed successfully');
    }
  };

  const handleCancel = async () => {
    if (!hasSubmittedRef.current) {
      await cleanupDraftUploads(photosMetaRef.current);
      cleanupDoneRef.current = true;
    }

    if (photosToDelete.length > 0) {
      setPhotosMeta(originalExistingPhotos);
      setPhotosToDelete([]);
      message.info('Changes cancelled. All photos restored.');
    }
    hasSubmittedRef.current = false;
    setModalOpen(false);
  };

  const getPopupContainer = (triggerNode) => triggerNode.parentElement || document.body;

  if (!isModalOpen) return null;

  return (
    <Modal
      title="Edit General Information"
      open={isModalOpen}
      onCancel={handleCancel}
      onOk={handleSubmit}
      centered
      confirmLoading={isLoading}
      okButtonProps={{
        disabled: hasUploadingPhotos,
        loading: isLoading,
      }}
      okText="Update Asset"
      styles={{
        body: {
          maxHeight: 'calc(90vh - 120px)',
          overflow: 'hidden',
        },
        header: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 16,
          fontWeight: 600,
          fontSize: 18,
        },
        footer: {
          display: 'flex',
          justifyContent: 'end',
          gap: 16,
          alignItems: 'center',
          padding: 8,
        },
      }}
      cancelText="Cancel"
      width={600}
    >
      <div
        style={{
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          padding: 24,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Divider orientation="left">
            <Notebook size={16} style={{ marginRight: 8 }} />
            Name
            <Tooltip title="Change the asset's name">
              <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
            </Tooltip>
          </Divider>
          <Input
            value={formState.name}
            onChange={(e) => onChangeField('name', e.target.value)}
            placeholder="Enter the asset name..."
          />

          <Divider orientation="left">
            <Notebook size={16} style={{ marginRight: 8 }} />
            Notes
            <Tooltip title="Any additional remarks about this asset">
              <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
            </Tooltip>
          </Divider>
          <TextArea
            rows={3}
            value={formState.note}
            onChange={(e) => onChangeField('note', e.target.value)}
            placeholder="Enter any additional notes..."
            autoSize={{ minRows: 3, maxRows: 5 }}
          />

          <Divider orientation="left">
            <CalendarIcon size={16} style={{ marginRight: 8 }} />
            Date Information
          </Divider>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={{ fontWeight: 500 }}>Next Maintenance</label>
              <DatePicker
                showTime
                style={{ width: '100%' }}
                placeholder="Select date and time..."
                value={formState.next_maintenance ? dayjs(formState.next_maintenance) : null}
                onChange={(date) => onChangeField('next_maintenance', date ? date.toDate() : null)}
                getPopupContainer={getPopupContainer}
                allowClear
                inputReadOnly
              />
            </div>

            <div>
              <label style={{ fontWeight: 500 }}>Installed At</label>
              <DatePicker
                showTime
                style={{ width: '100%' }}
                placeholder="Select installation date..."
                value={formState.installed_at ? dayjs(formState.installed_at) : null}
                onChange={(date) => onChangeField('installed_at', date ? date.toDate() : null)}
                getPopupContainer={getPopupContainer}
                allowClear
                inputReadOnly
              />
            </div>
          </div>

          <Divider orientation="left">
            <User size={16} style={{ marginRight: 8 }} />
            Asset Tracking
            <Tooltip title="Who recorded this asset information">
              <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
            </Tooltip>
          </Divider>
          <Select
            placeholder="Select recorder..."
            value={formState.created_by}
            onChange={(value) => onChangeField('created_by', value)}
            allowClear
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            getPopupContainer={getPopupContainer}
          >
            {userOptions?.map((user) => (
              <Select.Option key={user.value} value={user.value}>
                {user.label}
              </Select.Option>
            ))}
          </Select>
        </div>

        <Divider orientation="left">
          <InboxOutlined style={{ marginRight: 8 }} />
          Photos
          <Tooltip title="Upload or manage asset photos. New photos are stored as Base64 in database.">
            <InfoCircleOutlined style={{ marginLeft: 8, color: '#999' }} />
          </Tooltip>
        </Divider>

        {photosToDelete.length > 0 && (
          <div
            style={{
              padding: 8,
              backgroundColor: '#fff2e8',
              border: '1px solid #ffbb96',
              borderRadius: 4,
              marginBottom: 16,
            }}
          >
            <strong>⚠️ Warning:</strong> {photosToDelete.length > 0 && photosToDelete.length}{' '}
            photo(s) will be permanently deleted when you save.
          </div>
        )}

        <Upload.Dragger
          maxCount={1}
          beforeUpload={handleDraggerUpload}
          accept=".png,.jpg,.jpeg"
          showUploadList={false}
          name="photo"
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag photo to this area to upload</p>
          <p className="ant-upload-hint">Acceptable extensions are .png, .jpg and .jpeg</p>
        </Upload.Dragger>

        <div
          style={{
            marginTop: 16,
            padding: 8,
            border: '1px solid #f0f0f0',
            borderRadius: 4,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: 12,
          }}
        >
          {photosMeta.map((photo, idx) => {
            const statusMeta = STATUS_STYLES[photo.status];
            return (
              <div
                key={photo.id || photo.tempId || idx}
                style={{
                  position: 'relative',
                  height: 150,
                  backgroundColor: '#fafafa',
                  borderRadius: 6,
                  border: '1px solid #ddd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {statusMeta && (
                  <Tag
                    color={statusMeta.color}
                    style={{ position: 'absolute', top: 8, left: 8, zIndex: 10 }}
                  >
                    {statusMeta.label}
                  </Tag>
                )}
                {photo.status === PHOTO_STATUS.ERROR && (
                  <Button
                    type="text"
                    size="small"
                    danger
                    onClick={() => retryPhotoUpload(photo.tempId)}
                    style={{ position: 'absolute', bottom: 8, left: 8, zIndex: 10 }}
                    disabled={isLoading}
                  >
                    Retry
                  </Button>
                )}
                <img
                  src={getPhotoDisplayUrl(photo)}
                  alt={`Asset ${idx + 1}`}
                  style={{
                    height: 148,
                    width: '100%',
                    objectFit: 'cover',
                    borderRadius: 6,
                    padding: 1,
                  }}
                />
                <Tooltip
                  title={photo.isExisting ? '⚠️ Photo will be permanently deleted' : 'Delete photo'}
                >
                  <Button
                    icon={<DeleteOutlined />}
                    size="small"
                    shape="round"
                    onClick={() => handleDeletePhoto(idx)}
                    style={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}
                    danger={photo.isExisting}
                    disabled={
                      isLoading ||
                      photo.status === PHOTO_STATUS.DELETING ||
                      photo.status === PHOTO_STATUS.UPLOADING
                    }
                  />
                </Tooltip>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

export default EditGeneral;
